import React from "react";
import { useNavigate } from "react-router";

import { useTable } from "@refinedev/react-table";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { FESTIVAL_KIND_LABEL, dayRange, labelOf } from "@/lib/tina/festivals";
import {
  StatusSelect,
  TypeSelect,
} from "@/pages/tina/festivals/components/festival-filters";
import { FestivalStatusBadge } from "@/pages/tina/festivals/components/festival-status-badge";
import { OpenRowButton } from "@/pages/tina/festivals/components/open-row-button";
import {
  type StatusChoice,
  type TypeChoice,
  isStatusChoice,
  isTypeChoice,
  statusFilterValue,
} from "@/pages/tina/festivals/lib/list-filters";
import { pagePath } from "@/pages/tina/festivals/lib/paths";
import { usePersistedState } from "@/pages/tina/hooks/use-persisted-state";
import type { FestivalListRow } from "@/types/tina-festival-page";

const PAGE_SIZE = 50;

/** The two picks survive opening a festival and coming back, as the venue audit's do. */
const STATUS_STORAGE_KEY = "tina-festivals:status";
const TYPE_STORAGE_KEY = "tina-festivals:type";

/**
 * Nothing here carries a column filter or a sort. `GET /festivals/pages` takes exactly `status`, `kind`, `limit` and
 * `offset`, the order is fixed (most recently updated first) and the page owns both filters, so a header that looked
 * filterable or sortable would silently do nothing.
 */
const COLUMNS: ColumnDef<FestivalListRow>[] = [
  {
    id: "festival",
    header: "Festival",
    size: 300,
    cell: ({ row }) => (
      <span className="text-sm font-medium">{row.original.name}</span>
    ),
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    id: "type",
    header: "Type",
    size: 120,
    cell: ({ row }) => (
      <span className="text-sm">
        {labelOf(FESTIVAL_KIND_LABEL, row.original.kind)}
      </span>
    ),
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    id: "city",
    header: "City",
    size: 140,
    cell: ({ row }) => (
      <span className="text-sm">{row.original.city ?? "—"}</span>
    ),
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    id: "dates",
    header: "Dates",
    size: 170,
    cell: ({ row }) => (
      <span className="text-sm">
        {dayRange(row.original.first_day, row.original.last_day) || "—"}
      </span>
    ),
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    id: "status",
    header: "Status",
    size: 200,
    cell: ({ row }) => (
      <FestivalStatusBadge
        status={row.original.status}
        label={row.original.status_label}
      />
    ),
    enableSorting: false,
    enableColumnFilter: false,
  },
  {
    // The way into a festival, as on the HITL queue; the table pins it right.
    id: "actions",
    size: 70,
    header: "Actions",
    cell: ({ row }) => (
      <OpenRowButton
        to={pagePath(row.original.record_id)}
        label="Open festival"
      />
    ),
    enableSorting: false,
    enableColumnFilter: false,
  },
];

/**
 * `/tina/festivals`: one row per festival Tina found, with its status. Laid out as the HITL queue is: a title with two
 * selects on its right, one table with a right-pinned Actions column under them, an eye button and a double click on a
 * row to open it.
 *
 * Both filters are server-side `permanent` ones and are emitted on every render, `undefined` while cleared and never
 * omitted (Refine keeps a stale internal copy of a filter that stops being emitted). A cleared status is Tina's own
 * default set: the page does not know what "needs a person" holds.
 */
export const FestivalsList = () => {
  const navigate = useNavigate();
  const [persistedStatus, setStatus] = usePersistedState<string | null>(
    STATUS_STORAGE_KEY,
    null,
  );
  const [persistedType, setType] = usePersistedState<string | null>(
    TYPE_STORAGE_KEY,
    null,
  );
  // Narrowed on read, not trusted: localStorage can hold a pick a newer or older build wrote, and the route answers an
  // unknown status or kind with a 422, not an empty page.
  const status: StatusChoice = isStatusChoice(persistedStatus)
    ? persistedStatus
    : "needs_a_person";
  const type: TypeChoice | null = isTypeChoice(persistedType)
    ? persistedType
    : null;

  const table = useTable<FestivalListRow>({
    columns: COLUMNS,
    // The eye button stays reachable when the row scrolls sideways, as on the HITL queue.
    initialState: { columnPinning: { right: ["actions"] } },
    refineCoreProps: {
      // Resolved by `RESOURCE_PATHS` to `/festivals/pages`.
      resource: "tina-festival-pages",
      pagination: { pageSize: PAGE_SIZE },
      filters: {
        permanent: [
          { field: "status", operator: "in", value: statusFilterValue(status) },
          { field: "kind", operator: "eq", value: type ?? undefined },
        ],
      },
    },
    meta: {
      onDoubleClick: (
        _event: React.MouseEvent,
        row: { original: FestivalListRow },
      ) => {
        void navigate(pagePath(row.original.record_id));
      },
    },
  });

  // Back to page 1 whenever a filter changes, so a pick never strands the table on an out-of-range page of a smaller
  // result.
  const setCurrentPage = table.refineCore.setCurrentPage;
  const previous = React.useRef({ status, type });
  React.useEffect(() => {
    if (previous.current.status === status && previous.current.type === type) {
      return;
    }
    previous.current = { status, type };
    setCurrentPage(1);
  }, [status, type, setCurrentPage]);

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Festivals</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            One row per festival Tina found. Open one to approve it, publish it
            or change it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusSelect value={status} onChange={setStatus} />
          <TypeSelect value={type} onChange={setType} />
        </div>
      </div>
      <DataTable table={table} />
    </div>
  );
};
